import { useEffect, useState } from "preact/hooks";
import type { FlowState } from "../flow/machine";
import type { InstallRunner } from "../flow/runner";
import { t } from "../i18n";
import { JobProgress } from "./GroupWidgets";

/**
 * Step 5b: this phone joins the group. Asks for the SIM number (prefilled when the cloud
 * learned it at enrollment — fastboot cannot read a SIM) and the licence attestation, then
 * shows the chain being reprogrammed until both affected phones read back the desired state.
 */
export function MemberStep({ runner, s }: { runner: InstallRunner; s: FlowState }) {
  const [number, setNumber] = useState(() => runner.prefillNumber());
  const [attested, setAttested] = useState(false);
  const [attestedBy, setAttestedBy] = useState("");
  const [busy, setBusy] = useState(false);
  const m = s.member;
  const jobId = m?.jobId;
  useEffect(() => {
    // Reload mid-job: keep following it.
    if (jobId && !runner.store.state.member?.members && !runner.store.state.error) void runner.pollMemberJob();
  }, [runner, jobId]);
  useEffect(() => {
    if (!number) setNumber(runner.prefillNumber());
  }, [runner, s.enrollment?.simNumber, number]);

  const submit = async (e: Event) => {
    e.preventDefault();
    setBusy(true);
    await runner.submitMember({ number, attested, attestedBy });
    setBusy(false);
  };
  const position = s.position ?? 0;
  const group = s.group;
  const previous = position - 1;

  return (
    <div class="panel">
      <h2 tabIndex={-1}>{t("member.heading", { group: group?.name ?? "", position })}</h2>
      {s.intent === "create" ? <p>{t("member.intro_create", { number: group?.mainNumber ?? "" })}</p> : s.intent === "replace" ? <p>{t("member.intro_replace", { position })}</p> : <p>{t("member.intro_add", { position, previous })}</p>}
      {!m ? (
        <form onSubmit={(e) => void submit(e)} class="stack">
          <div>
            <label for="sim-number">{t("member.number_label")}</label>
            <input id="sim-number" class="ltr" type="tel" value={number} placeholder="+9715…" required aria-describedby="sim-number-hint" onInput={(e) => setNumber((e.target as HTMLInputElement).value)} />
            <p id="sim-number-hint" class="hint">{s.enrollment?.simNumber ? t("member.number_prefilled") : t("member.number_hint")}</p>
          </div>
          <section class="notice" aria-labelledby="attest-h">
            <h3 id="attest-h">{t("member.attest_heading")}</h3>
            <label style="font-weight:400">
              <input type="checkbox" checked={attested} onChange={(e) => setAttested((e.target as HTMLInputElement).checked)} required /> {t("member.attest_label", { number: number || "…" })}
            </label>
            <div>
              <label for="attested-by">{t("member.attested_by")}</label>
              <input id="attested-by" type="text" value={attestedBy} required onInput={(e) => setAttestedBy((e.target as HTMLInputElement).value)} />
            </div>
            <p class="hint">{t("member.attest_hint")}</p>
          </section>
          <div class="row">
            <button type="submit" class="primary" disabled={busy || !number.trim() || !attested || !attestedBy.trim()}>{s.intent === "replace" ? t("member.submit_replace") : t("member.submit")}</button>
          </div>
        </form>
      ) : (
        <>
          <p class="muted">{t("member.programming", { number: m.number })}</p>
          <ul class="hint">
            {s.intent !== "create" && previous >= 1 && <li>{t("member.expect_previous", { position: previous, next: position })}</li>}
            <li>{t("member.expect_new", { position })}</li>
            <li>{t("member.expect_readback")}</li>
          </ul>
          <JobProgress job={m.job} positions={m.affected} />
          {s.error?.code === "forwarding_out_of_sync" || s.error?.code === "chain_job_failed" ? (
            <button type="button" class="primary" onClick={() => { runner.retry(); void runner.repairChain(); }}>{t("member.repair")}</button>
          ) : null}
        </>
      )}
    </div>
  );
}
